import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";

const SELECTION_KEY = "erimus_apoio_selected_patient";

export const readStoredSelection = () => {
  try {
    return localStorage.getItem(SELECTION_KEY) || "";
  } catch {
    return "";
  }
};

export const clearStoredSelection = () => {
  try {
    localStorage.removeItem(SELECTION_KEY);
  } catch {
    /* armazenamento indisponível */
  }
};

const storeSelection = (id) => {
  try {
    if (id) localStorage.setItem(SELECTION_KEY, id);
    else localStorage.removeItem(SELECTION_KEY);
  } catch {
    /* armazenamento indisponível */
  }
};

const PatientContext = createContext(null);

/** Contexto global: um único paciente selecionado por vez em todo o app Rede de Apoio. */
export const PatientProvider = ({ children }) => {
  const { api, user, forceSignOut, logout } = useAuth();
  const [patients, setPatients] = useState(null);
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [error, setError] = useState("");
  // Epoch muda a cada troca de paciente: invalida caches e descarta respostas atrasadas.
  const [epoch, setEpoch] = useState(0);
  const abortRef = useRef(null);

  const abortPending = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
  }, []);

  if (!abortRef.current) abortRef.current = new AbortController();

  const loadPatients = useCallback(
    async ({ keepSelection = true } = {}) => {
      try {
        const { data } = await api.get("/network/patients");
        setPatients(data);
        setError("");
        setSelectedPatientId((current) => {
          const desired = keepSelection ? current || readStoredSelection() : "";
          const stillValid = data.some((p) => p.patient_id === desired);
          const next = stillValid ? desired : data.length === 1 ? data[0].patient_id : "";
          storeSelection(next);
          return next;
        });
        return data;
      } catch (e) {
        if (e.response?.status === 401) {
          forceSignOut();
          return [];
        }
        setError(formatApiError(e.response?.data?.detail));
        setPatients([]);
        return [];
      }
    },
    [api, forceSignOut]
  );

  useEffect(() => {
    if (user) loadPatients();
  }, [user, loadPatients]);

  const selectPatient = useCallback(
    (patientId) => {
      if (!patientId || patientId === selectedPatientId) return;
      abortPending();
      storeSelection(patientId);
      setSelectedPatientId(patientId);
      setEpoch((e) => e + 1);
    },
    [abortPending, selectedPatientId]
  );

  /** Vínculo revogado/expirado: bloqueia, limpa e reavalia os vínculos restantes. */
  const handleAccessLost = useCallback(async () => {
    abortPending();
    setSelectedPatientId("");
    clearStoredSelection();
    setEpoch((e) => e + 1);
    return loadPatients({ keepSelection: false });
  }, [abortPending, loadPatients]);

  const signOut = useCallback(async () => {
    abortPending();
    clearStoredSelection();
    setPatients(null);
    setSelectedPatientId("");
    await logout();
  }, [abortPending, logout]);

  const value = useMemo(() => {
    const selectedPatient = (patients || []).find((p) => p.patient_id === selectedPatientId) || null;
    return {
      patients,
      selectedPatientId,
      selectedPatient,
      epoch,
      error,
      loadPatients,
      selectPatient,
      handleAccessLost,
      signOut,
      getSignal: () => abortRef.current?.signal,
    };
  }, [patients, selectedPatientId, epoch, error, loadPatients, selectPatient, handleAccessLost, signOut]);

  return <PatientContext.Provider value={value}>{children}</PatientContext.Provider>;
};

export const useSelectedPatient = () => useContext(PatientContext);

/** Busca dados sempre no escopo do paciente selecionado, sem exibir resíduo do anterior. */
export const usePatientScopedData = (path) => {
  const { api, forceSignOut } = useAuth();
  const { selectedPatientId, epoch, getSignal, handleAccessLost } = useSelectedPatient();
  const [state, setState] = useState({ data: null, blocked: "" });

  const cacheKey = `${selectedPatientId}:${epoch}:${path}`;
  const keyRef = useRef(cacheKey);
  keyRef.current = cacheKey;

  const load = useCallback(async () => {
    if (!selectedPatientId) return;
    const key = keyRef.current;
    setState({ data: null, blocked: "" });
    try {
      const { data } = await api.get(`/network/patients/${selectedPatientId}${path}`, {
        signal: getSignal(),
      });
      if (keyRef.current !== key) return;
      setState({ data, blocked: "" });
    } catch (e) {
      if (e.code === "ERR_CANCELED" || e.name === "CanceledError") return;
      if (keyRef.current !== key) return;
      if (e.response?.status === 401) {
        forceSignOut();
        return;
      }
      if (e.response?.status === 403) {
        setState({ data: false, blocked: formatApiError(e.response?.data?.detail) });
        handleAccessLost();
        return;
      }
      setState({ data: false, blocked: formatApiError(e.response?.data?.detail) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, forceSignOut, getSignal, handleAccessLost, path, selectedPatientId]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  return { ...state, reload: load, cacheKey };
};
